// Every line a diner reads about the app fee lives here, so the button, the payment sheet, the line
// under the button and the friend's total all say the same thing. Shared by the server and the
// browser bundle, so it stays free of Node and DOM APIs. Never "free" in diner copy (docs/brand.md),
// with one exception Deuce made on Oct 21, 2026: the note under the host's "Ask friends to pay"
// button (FEE_NOTE_NONE and WAIVER_ON_OTHER below, allowlisted by exact text in
// scripts/copy-allowlist.json). Everywhere else a split with nothing to pay says "no fee".
// While pricing is off (PRICING_ENABLED, src/pricing.ts) the note shows neither: only the picked count,
// then the pay-back line (src/client/views/lock.ts). Both stay here, allowlisted, for when pricing returns.
// No brand name in these lines; the app fee is the app's.
import type { Cents } from './types.ts';
import type { FeeGroup } from './experiments.ts';
import { fmt } from './money.ts';

/** "38¢" under a dollar, "$1" on a whole dollar, else "$1.49". */
export function feeAmount(cents: Cents): string {
  if (cents < 100) return `${cents}¢`;
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** The host's button, in every fee group. No fee words on it: the note under it says what it costs. */
export const ASK_FRIENDS_TO_PAY = 'Ask friends to pay';

/** The payment sheet's title, and the line under it. */
export const paySheetTitle = (cents: Cents): string => `App fee: ${feeAmount(cents)}`;
export const PAY_SHEET_SUB = 'Not the check. You already paid that.';

// ---------- the note under "Ask friends to pay" (at most 2 short lines, plus the picked count) ----------
// The only place diner copy may say "free" (Deuce, Oct 21, 2026). Keep these exact strings: the copy
// lint allowlist matches them in this file and nowhere else.

/** The fee note on a split with nothing to pay (a referral, credit, code, table of 2, group B, fee off). */
export const FEE_NOTE_NONE = 'This split is free.';
/** Under the button on a newer open split, when the host's older open split is the one with no fee. */
export const WAIVER_ON_OTHER = 'Your free split is your other open one.';

/** The fee note: "This split is free." or "This split is $1.49." Shown while people are still picking. */
export const feeNote = (cents: Cents): string => (cents > 0 ? `This split is ${feeAmount(cents)}.` : FEE_NOTE_NONE);

/**
 * What friends pay the host back, once everyone has picked. `back` is the yellow banner's number
 * (in group C it includes the friends' fee shares). Empty when it's $0.00 (nobody has joined).
 *   no fee: "Your friends pay you back $28.16."  (1 friend: "Your friend pays you back $28.16.")
 *   A:      "Your friends pay you back $28.16. You pay the $1.49 app fee."
 *   C:      "Your 2 friends pay you back $29.66. That covers the $1.49, so it costs you $0."
 */
export function payBackLine(group: FeeGroup | undefined, friends: number, back: Cents, feeCents: Cents): string {
  if (!(back > 0)) return '';
  const one = friends === 1;
  const who = one ? 'Your friend pays' : group === 'C' && feeCents > 0 && friends > 1 ? `Your ${friends} friends pay` : 'Your friends pay';
  const head = `${who} you back ${fmt(back)}.`;
  if (!(feeCents > 0)) return head;
  if (group === 'C') return `${head} That covers the ${feeAmount(feeCents)}, so it costs you $0.`;
  return `${head} You pay the ${feeAmount(feeCents)} app fee.`;
}

/** The friend's line in their total. C: "+ 38¢ app fee". B: "+ $1 thanks" (B is off for now). */
export function friendFeeLine(group: FeeGroup | undefined, cents: Cents): string {
  if (!cents) return '';
  if (group === 'C') return `+ ${feeAmount(cents)} app fee`;
  if (group === 'B') return `+ ${feeAmount(cents)} thanks`;
  return '';
}

/** On the host's first screen, under the scan button. Text only: "card points", never rewards or cash back. */
export const POINTS_LINE = 'Put the check on your card. Keep the card points. The table pays you back.';

/** After totals are sent, for a host with a referral code. Only because a referred friend really earns them a split with no fee. */
export const REFERRAL_LINE = 'Invite a friend. Your next split has no fee.';
